import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { Text, CardHeader } from '@amzn/storm-ui';
import { Form } from '@amzn/storm-ui-column-form-kit';
import { MessageBundle } from '@amzn/arb-tools';
import InputController from '../../../shared/components/form/InputController';
import { RegistrationFormCard } from '../../../shared/features/account-registration/style';
import {
  PHONE_VALIDATION,
  WEBSITE_VALIDATION,
} from '../../../shared/components/form/constants';
import { EMAIL_VALIDATION_REGEX } from '../../../shared/constants';
import { RequestPageContext } from '../../../shared/services/activation/activation';
import {
  BILLING_HEADER_KEY,
  COMPANY_NAME_LABEL_KEY,
  CONTACT_NAME_LABEL_KEY,
  PRIMARY_EMAIL_LABEL_KEY,
  PHONE_NUMBER_LABEL_KEY,
  WEBSITE_LABEL_KEY,
  BUSINESS_REGISTRATION_NUMBER_LABEL_KEY,
  ADDRESS_LINE_1_LABEL_KEY,
  ADDRESS_LINE_2_LABEL_KEY,
  POSTAL_CODE_LABEL_KEY,
  CITY_LABEL_KEY,
  OPTIONAL_LABEL_KEY,
  SELECT_PLACEHOLDER_KEY,
  REQUIRED_ERROR_KEY,
  EMAIL_ERROR_KEY,
  PHONE_ERROR_KEY,
  WEBSITE_ERROR_KEY,
  MAX_LENGTH_ERROR_50_KEY,
  MAX_LENGTH_ERROR_60_KEY,
  MAX_LENGTH_ERROR_100_KEY,
} from '../constants';
import CountryStateField from './CountryStateField';
import TaxFields from './TaxFields';

interface BillingProps {
  intl: MessageBundle;
  context: RequestPageContext;
}

const Billing: FC<BillingProps> = ({ intl, context }) => {
  const { watch } = useFormContext();
  const selectedCountry = watch('country') as string;
  const select = intl.getMessage(SELECT_PLACEHOLDER_KEY);
  const required = intl.getMessage(REQUIRED_ERROR_KEY);
  const optional = intl.getMessage(OPTIONAL_LABEL_KEY);
  const emailError = intl.getMessage(EMAIL_ERROR_KEY);
  const phoneError = intl.getMessage(PHONE_ERROR_KEY);
  const websiteError = intl.getMessage(WEBSITE_ERROR_KEY);
  const maxLengthError50 = intl.getMessage(MAX_LENGTH_ERROR_50_KEY);
  const maxLengthError60 = intl.getMessage(MAX_LENGTH_ERROR_60_KEY);
  const maxLengthError100 = intl.getMessage(MAX_LENGTH_ERROR_100_KEY);

  return (
    <RegistrationFormCard>
      <CardHeader padding="small">
        <Text size="xlarge">{intl.getMessage(BILLING_HEADER_KEY)}</Text>
      </CardHeader>
      <Form id="billing">
        <InputController
          label={intl.getMessage(COMPANY_NAME_LABEL_KEY)}
          id="company-name-input"
          controllerName="companyName"
          registerOptions={{
            required,
            maxLength: { value: 50, message: maxLengthError50 },
          }}
        />
        <InputController
          label={intl.getMessage(CONTACT_NAME_LABEL_KEY)}
          id="contact-name-input"
          controllerName="contactName"
          registerOptions={{
            required,
            maxLength: { value: 50, message: maxLengthError50 },
          }}
        />
        <InputController
          label={intl.getMessage(PRIMARY_EMAIL_LABEL_KEY)}
          id="contact-email"
          controllerName="contactEmail"
          registerOptions={{
            required,
            pattern: { value: EMAIL_VALIDATION_REGEX, message: emailError },
          }}
        />
        <InputController
          label={intl.getMessage(PHONE_NUMBER_LABEL_KEY)}
          id="contact-phone-number"
          controllerName="phoneNumber"
          registerOptions={{
            required,
            pattern: { value: PHONE_VALIDATION, message: phoneError },
          }}
        />
        <InputController
          label={intl.getMessage(WEBSITE_LABEL_KEY)}
          id="website-input"
          controllerName="website"
          registerOptions={{
            required,
            pattern: { value: WEBSITE_VALIDATION, message: websiteError },
          }}
        />
        <InputController
          label={intl.getMessage(BUSINESS_REGISTRATION_NUMBER_LABEL_KEY)}
          id="business-registration-number-input"
          controllerName="businessRegistrationNumber"
          optionalTag={optional}
        />
        <InputController
          label={intl.getMessage(ADDRESS_LINE_1_LABEL_KEY)}
          id="address-line-1"
          controllerName="addressLine1"
          registerOptions={{
            required,
            maxLength: { value: 100, message: maxLengthError100 },
          }}
        />
        <InputController
          label={intl.getMessage(ADDRESS_LINE_2_LABEL_KEY)}
          id="address-line-2"
          controllerName="addressLine2"
          optionalTag={optional}
          registerOptions={{
            maxLength: { value: 60, message: maxLengthError60 },
          }}
        />
        <InputController
          label={intl.getMessage(POSTAL_CODE_LABEL_KEY)}
          id="address-postalCode"
          controllerName="postalCode"
          registerOptions={{ required }}
        />
        <InputController
          label={intl.getMessage(CITY_LABEL_KEY)}
          id="address-city"
          controllerName="city"
          registerOptions={{ required }}
        />
        <CountryStateField
          intl={intl}
          context={context}
          selectedCountry={selectedCountry}
          selectPlaceholder={select}
          requiredMessage={required}
        />
        <TaxFields intl={intl} />
      </Form>
    </RegistrationFormCard>
  );
};

export default Billing;
